import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useDebounce } from '@uidotdev/usehooks'
import React, { useRef } from 'react'
import { useClickAway, useEvent } from 'react-use'

import { QUERY_KEYS } from '../../../constants/queryKeys'
import { useIsMinWidthMd } from '../../../hooks/useIsMinWidthMd'
import { useIsMinWidthXl } from '../../../hooks/useIsMinWidthXl'
import { isMacOS } from '../../../lib/helpers/miscHelpers'
import globalSearchService from '../../../lib/services/globalSearchService'
import DesktopSearchBox from './components/DesktopSearchBox'
import MobileSearchBox from './components/MobileSearchBox'
import { useGlobalSearch } from './hooks/useGlobalSearch'

const GlobalSearch: React.FC = () => {
  const globalSearch = useGlobalSearch()
  const debouncedSearchTerm = useDebounce(globalSearch.searchTerm, 400)
  const isMinWidthMd = useIsMinWidthMd()
  const isMinWidthXl = useIsMinWidthXl()
  const ref = useRef<HTMLDivElement>(null)

  const globalSearchQuery = useQuery({
    queryKey: QUERY_KEYS.globalSearch(debouncedSearchTerm),
    queryFn: () => globalSearchService.search(debouncedSearchTerm),
    refetchOnWindowFocus: false,
    staleTime: 20000,
    placeholderData: keepPreviousData,
  })

  useFocusShortcut()
  useClickAway(ref, globalSearch.hide)

  const width = isMinWidthXl ? '300px' : undefined

  return (
    <div
      ref={ref}
      className="d-flex flex-grow-1 flex-shrink-0 position-relative cursor-pointer"
      style={{ width: width, maxWidth: width }}
    >
      {isMinWidthMd ? (
        <DesktopSearchBox globalSearchQuery={globalSearchQuery} />
      ) : (
        <MobileSearchBox globalSearchQuery={globalSearchQuery} />
      )}
    </div>
  )
}

function useFocusShortcut() {
  const globalSearch = useGlobalSearch()

  useEvent('keydown', function (event: KeyboardEvent) {
    const shouldFocus = isMacOS()
      ? event.metaKey && event.key === 'k' // macOS: Cmd+K
      : event.ctrlKey && event.key === '/' // Windows & Linux: Ctrl+/

    if (shouldFocus) {
      globalSearch.focus()
    }
  })
}

export default GlobalSearch
